import { classNames } from 'primereact/utils';
import {
  Control,
  Controller,
  FieldErrors,
  FieldPath,
  FieldValues,
  UseFormWatch,
} from 'react-hook-form';
import { Divider } from 'primereact/divider';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import React, { ReactElement, useRef } from 'react';

export interface PasswordInputProps<TFieldValues> {
  fieldName: FieldPath<TFieldValues>;
  confirmFieldName: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
  errors: FieldErrors;
  watch: UseFormWatch<FieldValues>;
}

export default function PasswordInput<TFieldValues>(
  props: PasswordInputProps<TFieldValues>,
): ReactElement {
  const { fieldName, confirmFieldName, control, errors, watch } = props;
  const password = useRef({});
  password.current = watch('password', '');
  const minLength = 8;

  const validate = (value: any) =>
    value === password.current || 'The passwords do not match';

  const getFormErrorMessage = (name: string): React.Component =>
    errors[name] && <small className="p-error">{errors[name].message}</small>;

  const passwordHeader = <h6>Pick a password</h6>;
  const passwordFooter = (
    <>
      <Divider />
      <p className="p-mt-2">Suggestions</p>
      <ul className="p-pl-2 p-ml-2 p-mt-0" style={{ lineHeight: '1.5' }}>
        <li>At least one lowercase</li>
        <li>At least one uppercase</li>
        <li>At least one numeric</li>
        <li>Minimum {minLength} characters</li>
      </ul>
    </>
  );

  return (
    <div className="grid">
      <div className="p-field col-12 md:col-6">
        <span className="p-float-label">
          <Controller
            name={fieldName}
            control={control}
            rules={{
              required: 'Password is required.',
              minLength: {
                value: minLength,
                message: `Password must be at least ${minLength} characters`,
              },
            }}
            render={({ field, fieldState }) => (
              <Password
                {...field}
                id={field.name}
                inputClassName={`${
                  String(field.value).length >= 8 ? 'border-teal-300' : null
                }`}
                value={(field.value as any) || ''}
                toggleMask
                className={classNames({ 'p-invalid': fieldState.error })}
                header={passwordHeader}
                footer={passwordFooter}
              />
            )}
          />
          <label
            htmlFor={fieldName}
            className={classNames({ 'p-error': errors.name })}
          >
            Password
          </label>
        </span>
        {getFormErrorMessage(fieldName)}
      </div>
      <div className="p-field col-12 md:col-6 ">
        <span className="p-float-label">
          <Controller
            name={confirmFieldName}
            control={control}
            rules={{ validate }}
            render={({ field, fieldState }) => (
              <InputText
                {...field}
                id={field.name}
                type="password"
                value={(field.value as any) || ''}
                className={`${classNames({
                  'p-invalid': fieldState.error,
                })} ${
                  String(field.value).length >= 8 ? 'border-teal-300' : null
                }`}
              />
            )}
          />
          <label
            htmlFor={confirmFieldName}
            className={classNames({ 'p-error': errors.name })}
          >
            Password Confirm
          </label>
        </span>
        {getFormErrorMessage(confirmFieldName)}
      </div>
    </div>
  );
}
